import { location, onCleanup, onMount, reactive } from 'cdui-js';
import { deposits } from '../../../../api/funds';
import i18n from '../../../../i18n';
import { DiFiSkeleton, DiFiSkeletonParagraph } from '../../../../components/DiFi/DiFiSkeleton';
import { routeReplace } from '../../../../lib/browser';
import { userState } from '../../../../stores/user';
import { showActionSheet } from '../../components/ActionSheet';
import { ActionSheetCheckIcon } from '../../components/ActionSheet/CheckIcon';
import { BtnLoading } from '../../components/BtnLoading';
import { showModal } from '../../components/Modal';
import { PROOF_IMAGE_MAX_BYTES, UploadImage } from '../../components/Upload';
import { resultFailIcon as removeIcon } from '../../../../assets/commonIcons';
import { IconChevronDown, IconClose, IconCloseCircle, IconUploadPlus } from './icons';
import { DepositShell } from './Shell';
import {
  accountNumberOf,
  accountSubUserId,
  amountError,
  brokerAccountNum,
  brokerBankCode,
  depositUrl,
  errorText,
  homeUrl,
  isChequePath,
  isSecuritiesAccount,
  leaveSubmittedForm,
  localizedBankName,
  maskBankAccount,
  matchDepositAccount,
  preferredAccountNum,
  readDepositQuery,
  sanitizeAmount,
  setMarketBankCache,
  shouldLeaveForm,
  stockAccountLabel,
  blockFormReturn,
} from './shared';
import { fundRecordsUrl, getBackToAppUrl, getDepositAppQuery, getDepositEntry } from './appBridge';
import { ensureDepositAccounts, ensureDepositBanks } from './preload';
import './deposit.css';

type Proof = { localUrl: string; filePath: string };

const filePathOf = (data: any) => {
  if (typeof data?.filePath === 'string') return data.filePath.trim();
  if (typeof data?.data === 'string') return data.data.trim();
  if (typeof data?.data?.filePath === 'string') return data.data.filePath.trim();
  return '';
};

const emptyErrors = () => ({
  account: '',
  payee: '',
  payer: '',
  name: '',
  num: '',
  amount: '',
  voucher: '',
});

const formSkeleton = () => (
  <div class="form flex-column gap-12px">
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '48px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="30%" style={{ height: '16px', 'margin-top': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '44px', 'border-radius': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="40%" style={{ height: '16px', 'margin-top': '8px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '88px', 'border-radius': '8px' }} />
  </div>
);

export const DepositForm = () => {
  const state = reactive({
    accounts: [] as any[],
    accountNum: '',
    subUserID: '',
    brokerBanks: [] as any[],
    payeeIndex: 0,
    userBanks: [] as any[],
    marketBanks: [] as any[],
    payerKey: '',
    payerBankCode: '',
    payerName: '',
    payerAccount: '',
    payerLocked: false,
    amount: '',
    remark: '',
    proofs: [] as Proof[],
    uploading: false,
    submitting: false,
    pageLoading: true,
    errors: emptyErrors(),
  });

  const cheque = () => isChequePath();
  const currency = () => readDepositQuery().currency;
  const selectedAccount = () => state.accounts.find((item) => accountNumberOf(item) === state.accountNum);
  const payee = () => state.brokerBanks[state.payeeIndex];
  const payeeName = () => (payee() ? localizedBankName(payee(), state.marketBanks) : readDepositQuery().payeeBankName);
  const payeeAccount = () => (payee() ? brokerAccountNum(payee()) : readDepositQuery().payeeBankAccountNum);
  const payeeCode = () => {
    const row = payee();
    return (row ? brokerBankCode(row) : '') || readDepositQuery().payeeBankCode;
  };
  const payerName = () => {
    if (!state.payerKey) return '';
    if (state.payerKey.indexOf('user:') === 0) {
      const row = state.userBanks.find((item) => userKey(item) === state.payerKey);
      return localizedBankName(row || {}, state.marketBanks);
    }
    if (state.payerKey.indexOf('market:') === 0) {
      const code = state.payerKey.slice('market:'.length);
      const row = state.marketBanks.find((item) => String(item.bankCode) === code);
      return localizedBankName(row || { bankCode: code }, state.marketBanks);
    }
    const row = state.userBanks.find((item) => String(item.bankCode) === state.payerKey);
    return localizedBankName(row || { bankCode: state.payerKey }, state.marketBanks);
  };

  onMount(() => {
    if (shouldLeaveForm()) {
      location.routeTo(homeUrl());
      return;
    }
    void load();
  });

  onCleanup(() => {
    state.proofs.forEach((item) => URL.revokeObjectURL(item.localUrl));
  });

  const selectAccount = (list: any[], urlAccountNum: string, mode: string, modeType: string) => {
    const fromUrl = String(urlAccountNum || '').trim();
    const byUrl = fromUrl ? list.find((item) => accountNumberOf(item) === fromUrl) : null;
    const byType =
      mode === 'security' && (modeType === 'C' || modeType === 'M')
        ? list.find((item) => String(item?.type ?? '').trim() === modeType)
        : null;
    const found = byUrl || byType || list.find((item) => accountNumberOf(item) === preferredAccountNum('')) || list[0];
    if (found) {
      state.accountNum = accountNumberOf(found);
      state.subUserID = accountSubUserId(found);
      return;
    }
    state.accountNum = '';
    state.subUserID = '';
  };

  /** 首页已预拉时这里直接用缓存；直进填单页也会补拉 */
  const load = async () => {
    state.pageLoading = true;
    try {
      const query = readDepositQuery();
      const [list, banks] = await Promise.all([
        ensureDepositAccounts().catch(() => state.accounts),
        ensureDepositBanks(query.currency),
      ]);
      const entry = getDepositEntry();
      const visible = list.filter((item) => {
        if (!matchDepositAccount(item, entry.mode)) return false;
        if (!entry.mode && !cheque() && query.currency === 'CNY' && !isSecuritiesAccount(item)) return false;
        return true;
      });
      state.accounts = visible;
      selectAccount(visible, query.accountNum, entry.mode, entry.modeType);
      state.userBanks = banks.userBanks;
      state.brokerBanks = banks.brokerBanks;
      state.marketBanks = cheque() ? [] : banks.marketBanks;
      if (!cheque()) setMarketBankCache(banks.marketBanks);

      const matched = banks.brokerBanks.findIndex((item) => brokerAccountNum(item) === query.payeeBankAccountNum);
      state.payeeIndex = matched >= 0 ? matched : 0;
    } finally {
      state.pageLoading = false;
    }
  };

  const openAccounts = () => {
    if (!state.accounts.length) return;
    const sheet = showActionSheet({
      title: i18n.FundH5.SelectAccount,
      content: () => (
        <div>
          {state.accounts.map((item) => {
            const num = accountNumberOf(item);
            const active = num === state.accountNum;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  state.accountNum = num;
                  state.subUserID = accountSubUserId(item);
                  state.errors.account = '';
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{stockAccountLabel(item)}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const selectUserBank = (row: any, close: () => void) => {
    if (cheque()) {
      state.payerKey = String(row.bankCode || '');
      state.payerBankCode = String(row.bankCode || '');
      state.payerLocked = false;
    } else {
      state.payerKey = userKey(row);
      state.payerBankCode = String(row.bankCode || '');
      state.payerName = String(row.bankAccountName || '');
      state.payerAccount = String(row.bankAccountNum || '');
      state.payerLocked = true;
    }
    state.errors.payer = '';
    close();
  };

  const selectMarketBank = (row: any, close: () => void) => {
    state.payerKey = `market:${row.bankCode}`;
    state.payerBankCode = String(row.bankCode || '');
    state.payerName = '';
    state.payerAccount = '';
    state.payerLocked = false;
    state.errors.payer = '';
    close();
  };

  const openPayer = () => {
    if (cheque() && !state.userBanks.length) {
      showModal({
        title: i18n.FundH5.WarmTip,
        content: () => i18n.FundH5.NoBoundBank,
        confirmText: i18n.FundH5.GotIt,
      });
      return;
    }
    const sheet = showActionSheet({
      title: i18n.H5Deposit.PayerBankTitle,
      content: () => (
        <div>
          {cheque() ? null : <div class="font-s-S color padding-y-XS">{i18n.H5Deposit.CurrentBank}</div>}
          {state.userBanks.length ? (
            state.userBanks.map((row) => {
              const key = cheque() ? String(row.bankCode || '') : userKey(row);
              const active = state.payerKey === key;
              return (
                <div
                  class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                  style={!cheque() ? { height: '72px' } : undefined}
                  onclick={() => selectUserBank(row, () => sheet.close())}
                >
                  <div class="action-sheet-option-label">
                    <div>{localizedBankName(row, state.marketBanks)}</div>
                    {cheque() ? null : (
                      <div class="color font-s-XS">{maskBankAccount(String(row.bankAccountNum || ''))}</div>
                    )}
                  </div>
                  {active ? <ActionSheetCheckIcon /> : null}
                </div>
              );
            })
          ) : (
            <div class="font-s-S color padding-y">{i18n.FundH5.NoBoundBank}</div>
          )}
          {cheque() ? null : <div class="font-s-S color padding-y-XS">{i18n.H5Deposit.OtherBanks}</div>}
          {cheque()
            ? null
            : state.marketBanks.map((row) => {
                const key = `market:${row.bankCode}`;
                const active = state.payerKey === key;
                return (
                  <div
                    class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                    onclick={() => selectMarketBank(row, () => sheet.close())}
                  >
                    <div class="action-sheet-option-label">{localizedBankName(row, state.marketBanks)}</div>
                    {active ? <ActionSheetCheckIcon /> : null}
                  </div>
                );
              })}
        </div>
      ),
    });
  };

  const openPayee = () => {
    if (state.brokerBanks.length <= 1) return;
    const sheet = showActionSheet({
      title: i18n.H5Deposit.PayeeBankTitle,
      content: () => (
        <div>
          {state.brokerBanks.map((row, index) => {
            const active = index === state.payeeIndex;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  state.payeeIndex = index;
                  state.errors.payee = '';
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{localizedBankName(row, state.marketBanks)}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const onUploadChange = (data: any) => {
    if (data.status === 'loading') {
      state.uploading = true;
      return;
    }
    state.uploading = false;
    if (data.status === 'error') return;
    const filePath = filePathOf(data);
    if (!filePath) {
      showModal({
        title: i18n.H5Deposit.UploadFailTitle,
        content: () => i18n.H5Deposit.UploadFailContent,
        confirmText: i18n.FundH5.GotIt,
      });
      return;
    }
    const localUrl = typeof data.localPreviewUrl === 'string' ? data.localPreviewUrl : '';
    state.proofs = state.proofs.concat([{ localUrl, filePath }]);
    state.errors.voucher = '';
  };

  const removeProof = (index: number) => {
    const item = state.proofs[index];
    if (item) URL.revokeObjectURL(item.localUrl);
    state.proofs = state.proofs.filter((_, i) => i !== index);
    state.errors.voucher = '';
  };

  const preview = (url: string) => {
    const modal = showModal({
      fullWidth: true,
      content: () => (
        <div class="bg-c" style={{ position: 'relative' }}>
          <button
            type="button"
            class="nav-back"
            style={{ position: 'absolute', top: '8px', right: '8px' }}
            onclick={() => modal.close()}
          >
            <IconClose />
          </button>
          <img src={url} alt="" style={{ width: '100%', display: 'block' }} />
        </div>
      ),
    });
  };

  const validate = () => {
    const errors = emptyErrors();
    if (!state.accountNum) errors.account = i18n.H5Deposit.ErrSelectDepositAccount;
    if (!payeeCode() && !payeeAccount()) errors.payee = i18n.H5Deposit.ErrSelectPayeeBank;
    if (!state.payerBankCode) errors.payer = i18n.H5Deposit.ErrSelectPayerBank;
    if (!state.payerName.trim()) errors.name = i18n.H5Deposit.ErrEnterAccountName;
    if (!state.payerAccount.trim()) errors.num = i18n.H5Deposit.ErrEnterAccountNo;
    errors.amount = amountError(state.amount);
    if (!state.proofs.length) errors.voucher = i18n.H5Deposit.ErrUploadRemittanceVoucher;
    state.errors = errors;
    return (
      !errors.account &&
      !errors.payee &&
      !errors.payer &&
      !errors.name &&
      !errors.num &&
      !errors.amount &&
      !errors.voucher
    );
  };

  const submit = async (close: () => void) => {
    if (state.submitting) return;
    state.submitting = true;
    const query = readDepositQuery();
    const body = {
      method: cheque() ? 'Cheque' : 'eBanking',
      accountNum: state.accountNum,
      currency: query.currency,
      subUserID: String(state.subUserID || ''),
      payerBankCode: state.payerBankCode,
      payerBankAccountName: state.payerName.trim(),
      payerBankAccountNum: state.payerAccount.trim(),
      payeeBankAccountNum: payeeAccount(),
      payeeBankCode: payeeCode(),
      amount: Number(state.amount),
      imagePaths: state.proofs.map((item) => item.filePath).filter(Boolean),
      remark: state.remark,
    };
    try {
      await deposits(body);
    } catch (error) {
      state.submitting = false;
      if (cheque()) {
        showModal({
          title: i18n.FundH5.SubmitFail,
          content: () => errorText(error) || i18n.FundH5.RetryLater,
          confirmText: i18n.FundH5.GotIt,
        });
      }
      return;
    }
    state.submitting = false;
    close();
    setTimeout(() => {
      showModal({
        title: i18n.FundH5.SubmitSuccess,
        disableClickMask: true,
        content: () => i18n.H5Deposit.SubmitSuccessDesc,
        cancelText: i18n.FundH5.ViewRecords,
        confirmText: i18n.FundH5.GotIt,
        onCancel: () => {
          const url = fundRecordsUrl('deposit', { accountNum: state.accountNum, isBackToApp: true });
          console.log('[h5-deposit] 查看记录 点击', {
            from: 'deposit',
            accountNum: state.accountNum,
            isBackToApp: true,
            targetUrl: url,
            backToAppUrl: getBackToAppUrl(),
            entryQuery: getDepositAppQuery(),
          });
          leaveSubmittedForm(url);
        },
        onConfirm: () => {
          blockFormReturn();
          routeReplace(homeUrl());
        },
      });
    }, 350);
  };

  const openConfirm = () => {
    if (state.uploading || !validate()) return;
    const sheet = showActionSheet({
      title: i18n.H5Deposit.ConfirmSheetTitle,
      contentClass: 'deposit-confirm-sheet',
      canClose: () => !state.submitting,
      content: () => (
        <div>
          <div class="flex-column gap-12px font-s-S">
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Deposit.LabelDepositAccount}</span>
              <span class="color-D">{stockAccountLabel(selectedAccount())}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Deposit.LabelTransferMethod}</span>
              <span class="color-D">{cheque() ? i18n.FundH5.MethodCheque : i18n.FundH5.MethodBank}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Deposit.PayerBankTitle}</span>
              <span class="color-D">{payerName()}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Deposit.PayeeBankTitle}</span>
              <span class="color-D">{payeeName()}</span>
            </div>
            <div class="flex justify-space-between gap-12px">
              <span class="color">{i18n.H5Deposit.LabelDepositFunds}</span>
              <span class="color-D">{`${state.amount} ${currency()}`}</span>
            </div>
          </div>
          <div class="deposit-confirm-actions">
            <button
              type="button"
              class="modal-panel-btn-cancel btn-rounded"
              disabled={state.submitting}
              onclick={() => {
                if (!state.submitting) sheet.close();
              }}
            >
              {i18n.FundH5.Cancel}
            </button>
            <button
              type="button"
              class="primary-btn btn-rounded"
              disabled={state.submitting}
              onclick={() => {
                void submit(() => sheet.close());
              }}
            >
              {state.submitting ? <BtnLoading /> : i18n.FundH5.Confirm}
            </button>
          </div>
        </div>
      ),
    });
  };

  return (
    <DepositShell
      title={cheque() ? i18n.FundH5.MethodCheque : i18n.H5Deposit.Title}
      footer={
        state.pageLoading ? null : (
          <div class="footer">
            <button type="button" class="btn-primary" disabled={state.uploading || state.submitting} onclick={openConfirm}>
              {state.submitting ? <BtnLoading /> : i18n.FundH5.Submit}
            </button>
          </div>
        )
      }
    >
      <DiFiSkeleton loading={state.pageLoading} template={formSkeleton()}>
      <div class="form">
        <button
          type="button"
          class={`account-trigger${state.accountNum ? '' : ' is-empty'}`}
          onclick={openAccounts}
        >
          {selectedAccount() ? stockAccountLabel(selectedAccount()) : i18n.H5Deposit.ErrSelectDepositAccount}
          <svg class="account-trigger-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M7.29957 9.68346C7.68874 10.0664 8.3131 10.0664 8.70227 9.68346L11.4334 6.99625C11.9437 6.49419 11.5882 5.626 10.8723 5.626H5.12952C4.41366 5.626 4.05816 6.49419 4.56843 6.99625L7.29957 9.68346Z"
              fill="currentColor"
            />
          </svg>
        </button>
        {state.errors.account ? <div class="err">{state.errors.account}</div> : null}

        <div class="field-group">
          <div class="block-title">{i18n.H5Deposit.SectionRemitInfo}</div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.PayeeBankTitle}<span class="star">*</span>
            </div>
            <div
              class={`picker${payeeName() ? '' : ' is-empty'}${state.errors.payee ? ' is-error' : ''}`}
              onclick={openPayee}
            >
              <span>{payeeName() || i18n.H5Deposit.ErrSelectPayeeBank}</span>
              {state.brokerBanks.length > 1 ? <IconChevronDown /> : null}
            </div>
            {state.errors.payee ? <div class="err">{state.errors.payee}</div> : null}
          </div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.LabelPayeeAccountNo}<span class="star">*</span>
            </div>
            <input class="input" readonly value={payeeAccount()} />
          </div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.PayerBankTitle}<span class="star">*</span>
            </div>
            <div
              class={`picker${payerName() ? '' : ' is-empty'}${state.errors.payer ? ' is-error' : ''}`}
              onclick={openPayer}
            >
              <span>{payerName() || i18n.H5Deposit.ErrSelectPayerBank}</span>
              <IconChevronDown />
            </div>
            {state.errors.payer ? <div class="err">{state.errors.payer}</div> : null}
          </div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.LabelAccountName}<span class="star">*</span>
            </div>
            <input
              class={`input${state.errors.name ? ' is-error' : ''}`}
              readonly={state.payerLocked}
              placeholder={i18n.H5Deposit.PhAccountName}
              value={state.payerName}
              oninput={(event) => {
                state.payerName = event.currentTarget.value;
                state.errors.name = '';
              }}
            />
            {state.errors.name ? <div class="err">{state.errors.name}</div> : null}
          </div>

          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.LabelAccountNo}<span class="star">*</span>
            </div>
            <input
              class={`input${state.errors.num ? ' is-error' : ''}`}
              readonly={state.payerLocked}
              inputmode={state.payerLocked || cheque() ? 'text' : 'numeric'}
              placeholder={i18n.H5Deposit.PhAccountNo}
              value={state.payerAccount}
              oninput={(event) => {
                state.payerAccount = event.currentTarget.value;
                state.errors.num = '';
              }}
            />
            {state.errors.num ? <div class="err">{state.errors.num}</div> : null}
          </div>
        </div>

        <div class="field-group">
          <div class="block-title">{i18n.H5Deposit.SectionAmount}</div>
          <div class="field">
            <div class="field-label">
              {i18n.H5Deposit.LabelAmount}<span class="star">*</span>
            </div>
            <div class={`input${state.errors.amount ? ' is-error' : ''}`}>
              <input
                class="amount-field"
                inputmode="decimal"
                placeholder={i18n.H5Deposit.PhDepositAmount}
                value={state.amount}
                oninput={(event) => {
                  const next = sanitizeAmount(event.currentTarget.value);
                  state.amount = next;
                  event.currentTarget.value = next;
                  state.errors.amount = '';
                }}
              />
              <div class="amount-side">
                {state.amount ? (
                  <button
                    type="button"
                    class="amount-clear"
                    onclick={() => {
                      state.amount = '';
                    }}
                  >
                    <IconCloseCircle />
                  </button>
                ) : null}
                <span>{currency()}</span>
              </div>
            </div>
            {state.errors.amount ? <div class="err">{state.errors.amount}</div> : null}
          </div>
        </div>

        <div class="field-group">
          <div class="block-title">{i18n.H5Deposit.SectionVoucher}</div>
          <div class="upload">
            {state.proofs.map((item, index) => (
              <div class="upload-slot">
                <img src={item.localUrl} alt="" onclick={() => preview(item.localUrl)} />
                <button
                  type="button"
                  class="upload-remove"
                  onclick={(event) => {
                    event.stopPropagation?.();
                    removeProof(index);
                  }}
                >
                  <img src={removeIcon} alt="" />
                </button>
              </div>
            ))}
            {state.proofs.length < 5 ? (
              <div class="upload-slot upload-add">
                <IconUploadPlus class="upload-add-icon" />
                <UploadImage
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    'z-index': 2,
                  }}
                  useFor="deposit"
                  busId={String(userState.id || '0')}
                  restrictImageType={true}
                  maxSize={PROOF_IMAGE_MAX_BYTES}
                  change={onUploadChange}
                />
              </div>
            ) : null}
          </div>
          {state.errors.voucher ? <div class="err">{state.errors.voucher}</div> : null}
        </div>

        <div class="field">
          <div class="field-label">{i18n.H5Deposit.RemarkOptional}</div>
          <input
            class="input"
            value={state.remark}
            oninput={(event) => {
              state.remark = event.currentTarget.value;
            }}
            maxlength={200}
          />
        </div>
      </div>
      </DiFiSkeleton>
    </DepositShell>
  );
};

const userKey = (row: any) => `user:${row?.bankCode || ''}:${row?.bankAccountNum || ''}`;

export const DepositBankStep2 = DepositForm;
export const DepositChequeStep2 = DepositForm;
